import type { InputHTMLAttributes, TextareaHTMLAttributes, SelectHTMLAttributes, ReactNode } from 'react';

interface GabInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  help?: string;
  required?: boolean;
}

export function GabInput({ label, error, help, required, className = '', id, ...rest }: GabInputProps) {
  const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
  return (
    <div className="form-group">
      {label && <label className={`form-label${required ? ' required' : ''}`} htmlFor={inputId}>{label}</label>}
      <input className={`form-input${error ? ' error' : ''} ${className}`} id={inputId} {...rest} />
      {help && !error && <span className="form-help">{help}</span>}
      {error && <span className="form-error" role="alert">{error}</span>}
    </div>
  );
}

interface GabTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  help?: string;
  required?: boolean;
}

export function GabTextarea({ label, error, help, required, className = '', id, ...rest }: GabTextareaProps) {
  const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
  return (
    <div className="form-group">
      {label && <label className={`form-label${required ? ' required' : ''}`} htmlFor={inputId}>{label}</label>}
      <textarea className={`form-textarea${error ? ' error' : ''} ${className}`} id={inputId} {...rest} />
      {help && !error && <span className="form-help">{help}</span>}
      {error && <span className="form-error" role="alert">{error}</span>}
    </div>
  );
}

interface GabNativeSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  help?: string;
  required?: boolean;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
}

export function GabNativeSelect({ label, error, help, required, options, placeholder, className = '', id, ...rest }: GabNativeSelectProps) {
  const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
  return (
    <div className="form-group">
      {label && <label className={`form-label${required ? ' required' : ''}`} htmlFor={inputId}>{label}</label>}
      <select className={`form-select${error ? ' error' : ''} ${className}`} id={inputId} {...rest}>
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {help && !error && <span className="form-help">{help}</span>}
      {error && <span className="form-error" role="alert">{error}</span>}
    </div>
  );
}

interface GabCheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
}

export function GabCheckbox({ label, className = '', ...rest }: GabCheckboxProps) {
  return (
    <label className={`form-check ${className}`}>
      <input type="checkbox" {...rest} />
      <span>{label}</span>
    </label>
  );
}

interface GabRadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
}

export function GabRadio({ label, className = '', ...rest }: GabRadioProps) {
  return (
    <label className={`form-check ${className}`}>
      <input type="radio" {...rest} />
      <span>{label}</span>
    </label>
  );
}
